import React, { useEffect, useState } from 'react';
import './Home.css';

const Home = ({ onSearch }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [isListening, setIsListening] = useState(false);
    const [recognition, setRecognition] = useState(null);

    useEffect(() => {
        // Initialize speech recognition
        if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
            const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
            const recognitionInstance = new SpeechRecognition();

            recognitionInstance.continuous = false;
            recognitionInstance.interimResults = false;
            recognitionInstance.lang = 'en-US';

            recognitionInstance.onresult = (event) => {
                const transcript = event.results[0][0].transcript;
                setSearchQuery(transcript);
                setIsListening(false);
            };

            recognitionInstance.onerror = (event) => {
                console.error('Speech recognition error:', event.error);
                setIsListening(false);
            };

            recognitionInstance.onend = () => {
                setIsListening(false);
            };

            setRecognition(recognitionInstance);
        }
    }, []);

    const handleSearch = () => {
        if (searchQuery.trim()) {
            // Call the parent component's onSearch function
            onSearch(searchQuery.trim());
        }
    };

    const handleSuggestionClick = (suggestion) => {
        setSearchQuery(suggestion);
    };

    const handleVoiceSearch = () => {
        if (recognition) {
            if (isListening) {
                recognition.stop();
                setIsListening(false);
            } else {
                recognition.start();
                setIsListening(true);
            }
        } else {
            alert('Speech recognition is not supported in your browser. Please use Chrome or Edge.');
        }
    };

    return (
        <div className="home">
            {/* Header */}
            <header className="header">
                <div className="header-content">
                    <div className="logo">
                        <i className="fas fa-search"></i>
                        <span>MedSearch</span>
                    </div>
                    <nav className="nav">
                        <a href="#home">Home</a>
                        <a href="#about">About</a>
                        <a href="#help">Help</a>
                    </nav>
                </div>
            </header>

            {/* Hero Section */}
            <section className="hero">
                <div className="hero-content">
                    <h1 className="hero-title">
                        Discover <span className="highlight">Medical Knowledge</span>
                    </h1>
                    <div className="search-container">
                        <p className="search-description">No Boolean operators. No complex syntax. Just search.</p>
                        <div className="search-bar">
                            <input
                                type="text"
                                placeholder="Search biomedical literature (e.g., 'best treatment for childhood asthma')"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                            />
                            <button
                                className={`voice-search ${isListening ? 'listening' : ''}`}
                                onClick={handleVoiceSearch}
                                title={isListening ? 'Stop listening' : 'Start voice search'}
                            >
                                <i className={`fas ${isListening ? 'fa-stop' : 'fa-microphone'}`}></i>
                            </button>
                        </div>
                        <button className="search-button" onClick={handleSearch}>
                            <i className="fas fa-search"></i>
                            Search PubMed Smartly
                        </button>
                    </div>
                </div>
            </section>

            {/* Smart Suggestions */}
            <section className="suggestions">
                <div className="suggestions-content">
                    <h2 className="suggestions-title">
                        <i className="fas fa-lightbulb"></i>
                        Smart Suggestions
                    </h2>
                    <div className="suggestion-tags">
                        {[
                            "COVID-19 vaccine efficacy",
                            "Alzheimer's disease biomarkers",
                            "Cancer immunotherapy advances",
                            "Diabetes management strategies",
                            "Pediatric mental health interventions"
                        ].map((suggestion, index) => (
                            <button
                                key={index}
                                className="suggestion-tag"
                                onClick={() => handleSuggestionClick(suggestion)}
                            >
                                {suggestion}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Feature Cards */}
            <section className="features">
                <div className="features-content">
                    <div className="feature-card">
                        <div className="feature-icon">
                            <i className="fas fa-brain"></i>
                        </div>
                        <h3>Semantic Search</h3>
                        <p>Our AI understands the meaning behind your query, not just keywords. Find relevant papers even when they use different terminology.</p>
                    </div>
                    <div className="feature-card">
                        <div className="feature-icon">
                            <i className="fas fa-compass"></i>
                        </div>
                        <h3>Guided Discovery</h3>
                        <p>Explore related concepts and research directions with intelligent recommendations that adapt to your interests.</p>
                    </div>
                    <div className="feature-card">
                        <div className="feature-icon">
                            <i className="fas fa-search-plus"></i>
                        </div>
                        <h3>Transparent Results</h3>
                        <p>Understand why papers are recommended with clear explanations of relevance and citation impact metrics.</p>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="footer">
                <div className="footer-content">
                    <div className="footer-links">
                        <a href="#terms">Terms</a>
                        <a href="#privacy">Privacy</a>
                        <a href="#contact">Contact</a>
                    </div>
                    <div className="footer-stats">
                        <span className="stats-dot"></span>
                        12,458 active searches today
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Home;
